/**
 * Video Carousel - High Performance 9:16 Video Scroller
 * Autoplay all visible videos simultaneously with side navigation
 */

class VideoCarousel extends HTMLElement {
  constructor() {
    super();
    
    this.track = this.querySelector('.video-carousel__track');
    this.items = this.querySelectorAll('.video-carousel__item');
    this.prevButton = this.querySelector('.video-carousel__button--prev');
    this.nextButton = this.querySelector('.video-carousel__button--next');
    this.deferredMedias = this.querySelectorAll('deferred-media');
    
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.scrollSnapEnabled = this.dataset.scrollSnap === 'true';
    
    this.scrollTimeout = null;
    this.visibleMedias = new Set();
    this.loadedMedias = new Set();
    
    // Observer for visible videos - play all that are visible
    this.observerOptions = {
      root: this.track,
      rootMargin: '0px',
      threshold: 0.5 // Video plays when 50% visible
    };
    
    // Observer for preloading (starts earlier)
    this.preloadObserverOptions = {
      root: this.track,
      rootMargin: '100px',
      threshold: 0
    };
    
    this.videoObserver = null;
    this.preloadObserver = null;
    this.resizeObserver = null;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    if (!this.track || this.items.length === 0) return;
    
    this.setupPreloadObserver();
    this.setupVideoObserver();
    this.setupNavigation();
    this.setupScrollHandler();
    this.setupClickHandler();
    this.setupKeyboardNavigation();
    this.setupResizeHandler();
  }

  /**
   * Preload Observer - Loads video content before it becomes visible
   */
  setupPreloadObserver() {
    this.preloadObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const deferredMedia = entry.target;
        
        if (entry.isIntersecting && !this.loadedMedias.has(deferredMedia)) {
          this.preloadVideo(deferredMedia);
        }
      });
    }, this.preloadObserverOptions);

    this.deferredMedias.forEach(media => {
      this.preloadObserver.observe(media);
    });
  }

  /**
   * Video Observer - Plays ALL visible videos simultaneously
   */
  setupVideoObserver() {
    this.videoObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const deferredMedia = entry.target;
        
        if (entry.isIntersecting) {
          // Add to visible set and play
          this.visibleMedias.add(deferredMedia);
          this.playVideo(deferredMedia);
          deferredMedia.closest('.video-carousel__item')?.classList.add('is-visible');
        } else {
          // Remove from visible set and pause
          this.visibleMedias.delete(deferredMedia);
          this.pauseVideo(deferredMedia);
          deferredMedia.closest('.video-carousel__item')?.classList.remove('is-visible');
        }
      });
    }, this.observerOptions);

    this.deferredMedias.forEach(media => {
      this.videoObserver.observe(media);
    });
  }

  /**
   * Preload video content (lazy load into DOM)
   */
  preloadVideo(deferredMedia) {
    if (deferredMedia.hasAttribute('loaded') || this.loadedMedias.has(deferredMedia)) {
      return;
    }

    deferredMedia.classList.add('loading');
    this.loadedMedias.add(deferredMedia);

    const loadContent = () => {
      const template = deferredMedia.querySelector('template');
      if (template) {
        const content = template.content.cloneNode(true);
        
        const video = content.querySelector('video');
        if (video) {
          video.muted = true;
          video.playsInline = true;
          video.setAttribute('muted', '');
          video.setAttribute('playsinline', '');
        }
        
        deferredMedia.appendChild(content);
        deferredMedia.setAttribute('loaded', '');
        deferredMedia.classList.remove('loading');
      }
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(loadContent, { timeout: 100 });
    } else {
      loadContent();
    }
  }

  /**
   * Play the video element
   */
  playVideo(deferredMedia) {
    if (!deferredMedia.hasAttribute('loaded')) {
      this.preloadVideo(deferredMedia);
    }

    const video = deferredMedia.querySelector('video');
    const iframe = deferredMedia.querySelector('iframe');
    
    if (video) {
      if (video.ended) {
        video.currentTime = 0;
      }
      
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          video.muted = true;
          video.play().catch(() => {});
        });
      }
    } else if (iframe) {
      const src = iframe.src;
      if (src.includes('youtube') && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func: 'playVideo', args: '' }),
          '*'
        );
      } else if (src.includes('vimeo') && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          JSON.stringify({ method: 'play' }),
          '*'
        );
      }
    }
  }

  /**
   * Pause the video element
   */
  pauseVideo(deferredMedia) {
    const video = deferredMedia.querySelector('video');
    const iframe = deferredMedia.querySelector('iframe');
    
    if (video) {
      video.pause();
    } else if (iframe && iframe.contentWindow) {
      const src = iframe.src;
      if (src.includes('youtube')) {
        iframe.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }),
          '*'
        );
      } else if (src.includes('vimeo')) {
        iframe.contentWindow.postMessage(
          JSON.stringify({ method: 'pause' }),
          '*'
        );
      }
    }
  }

  /**
   * Handle click on poster to toggle play/pause manually
   */
  setupClickHandler() {
    this.deferredMedias.forEach(media => {
      const poster = media.querySelector('.video-carousel__poster');
      
      if (poster) {
        poster.addEventListener('click', (e) => {
          e.preventDefault();
          
          if (!media.hasAttribute('loaded')) {
            this.preloadVideo(media);
            setTimeout(() => this.playVideo(media), 100);
            return;
          }
          
          const video = media.querySelector('video');
          if (video) {
            if (video.paused) {
              this.playVideo(media);
            } else {
              this.pauseVideo(media);
            }
          }
        });
      }
    });
  }

  /**
   * Setup navigation buttons
   */
  setupNavigation() {
    if (!this.prevButton || !this.nextButton) return;

    this.prevButton.addEventListener('click', () => {
      this.scrollToItem('prev');
    });

    this.nextButton.addEventListener('click', () => {
      this.scrollToItem('next');
    });
  }

  /**
   * Scroll to previous/next item
   */
  scrollToItem(direction) {
    const scrollAmount = this.items[0]?.offsetWidth + 16 || 300;
    const currentScroll = this.track.scrollLeft;
    
    const targetScroll = direction === 'next' 
      ? currentScroll + scrollAmount 
      : currentScroll - scrollAmount;

    this.track.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  }

  /**
   * Setup scroll handling
   */
  setupScrollHandler() {
    let ticking = false;
    let scrollEndTimeout;
    
    this.track.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          ticking = false;
        });
        ticking = true;
      }
      
      clearTimeout(scrollEndTimeout);
      scrollEndTimeout = setTimeout(() => {
        // Scroll ended
      }, 150);
    }, { passive: true });
  }

  /**
   * Setup keyboard navigation
   */
  setupKeyboardNavigation() {
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.scrollToItem('prev');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.scrollToItem('next');
      }
    });
  }

  /**
   * Handle resize
   */
  setupResizeHandler() {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {}, 100);
      });
      this.resizeObserver.observe(this.track);
    }
  }

  /**
   * Cleanup
   */
  cleanup() {
    if (this.videoObserver) {
      this.videoObserver.disconnect();
      this.videoObserver = null;
    }
    
    if (this.preloadObserver) {
      this.preloadObserver.disconnect();
      this.preloadObserver = null;
    }
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    
    clearTimeout(this.scrollTimeout);
    clearTimeout(this.resizeTimeout);
    
    this.deferredMedias.forEach(media => this.pauseVideo(media));
  }
}

// Define the custom element
customElements.define('video-carousel', VideoCarousel);

/**
 * Global visibility handler
 */
document.addEventListener('visibilitychange', () => {
  const carousels = document.querySelectorAll('video-carousel');
  
  carousels.forEach(carousel => {
    const deferredMedias = carousel.querySelectorAll('deferred-media');
    
    if (document.hidden) {
      deferredMedias.forEach(media => {
        const video = media.querySelector('video');
        if (video) video.pause();
      });
    } else {
      // Play all visible videos when tab becomes active
      deferredMedias.forEach(media => {
        const item = media.closest('.video-carousel__item');
        if (item?.classList.contains('is-visible')) {
          const video = media.querySelector('video');
          if (video) video.play().catch(() => {});
        }
      });
    }
  });
});
